import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../features/auth/hooks/useAuth';
import schoolService from '../services/schoolService';
import useParentStore from '../stores/parentStore';
import {
  cacheMatchesDeveloperSelection,
  getDeveloperSchoolSelection,
  resolveSchoolContextId,
  setDeveloperSchoolSelection,
} from '../config/developerWorkspace';

const SchoolContext = createContext();

// LocalStorage cache for school context (shared across tabs)
const SCHOOL_CACHE_KEY = 'swan_school_context';

/**
 * Save school context to localStorage (shared across tabs)
 */
const saveSchoolToCache = (userId, schoolData) => {
  try {
    if (userId && schoolData) {
      const cacheData = {
        ...schoolData,
        cachedAt: new Date().toISOString()
      };
      localStorage.setItem(`${SCHOOL_CACHE_KEY}_${userId}`, JSON.stringify(cacheData));
      console.log('[SchoolContext] 💾 Saved school context to localStorage (shared across tabs)');
    }
  } catch (error) {
    console.warn('[SchoolContext] Failed to cache school:', error);
  }
};

/**
 * Load school context from localStorage (shared across tabs)
 */
const loadSchoolFromCache = (userId) => {
  try {
    if (!userId) return null;

    const cached = localStorage.getItem(`${SCHOOL_CACHE_KEY}_${userId}`);
    if (cached) {
      const data = JSON.parse(cached);
      console.log('[SchoolContext] 📦 CACHE HIT - school from localStorage:', data.cachedAt);
      return data;
    }
  } catch (error) {
    // Clear corrupted cache
    try {
      if (userId) {
        localStorage.removeItem(`${SCHOOL_CACHE_KEY}_${userId}`);
      }
    } catch (e) {
      // Ignore cleanup errors
    }
  }
  return null;
};

/**
 * Hook to access the current school context
 * @returns {Object} School context with schoolId, school data, and loading state
 */
export const useSchool = () => {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error('useSchool must be used within a SchoolProvider');
  }
  return context;
};

/**
 * SchoolProvider - Provides school context based on the authenticated user's schoolId
 *
 * This provider:
 * 1. Loads the user's schoolId from their user document
 * 2. Fetches the school data
 * 3. Provides schoolId and school data to all child components
 */
export const SchoolProvider = ({ children }) => {
  const { user, initializing: authInitializing } = useAuth();

  // Load from cache SYNCHRONOUSLY before first render (instant display)
  const loadedSchoolData = user?.uid ? loadSchoolFromCache(user.uid) : null;
  const cachedSchoolData = loadedSchoolData &&
    cacheMatchesDeveloperSelection(loadedSchoolData, user?.uid)
    ? loadedSchoolData
    : null;

  const [schoolId, setSchoolId] = useState(() => cachedSchoolData?.schoolId || null);
  const [membershipSchoolId, setMembershipSchoolId] = useState(
    () => cachedSchoolData?.membershipSchoolId || cachedSchoolData?.schoolId || null
  );
  const [developerSchoolId, setDeveloperSchoolId] = useState(
    () => cachedSchoolData?.developerSchoolId || null
  );
  const [assignedSchoolId, setAssignedSchoolId] = useState(() => cachedSchoolData?.assignedSchoolId || null);
  const [school, setSchool] = useState(() => cachedSchoolData?.school || null);
  const [assignedSchool, setAssignedSchool] = useState(() => cachedSchoolData?.assignedSchool || null);
  const [permissions, setPermissions] = useState(() => cachedSchoolData?.permissions || null);
  const [menuItems, setMenuItems] = useState(() => cachedSchoolData?.menuItems || null);
  const [organizationalContext, setOrganizationalContext] = useState(() => cachedSchoolData?.organizationalContext || null);

  // If cache exists, we're not loading
  const [loading, setLoading] = useState(() => !cachedSchoolData);
  const [error, setError] = useState(null);

  // Track whether real-time listener has provided data
  // Once true, don't let Cloud Function overwrite permissions
  const listenerHasFiredRef = useRef(false);

  // Refs so the onSnapshot closure always compares against current values
  const schoolIdRef = useRef(schoolId);
  const membershipSchoolIdRef = useRef(membershipSchoolId);
  const assignedSchoolIdRef = useRef(assignedSchoolId);
  useEffect(() => { schoolIdRef.current = schoolId; }, [schoolId]);
  useEffect(() => { membershipSchoolIdRef.current = membershipSchoolId; }, [membershipSchoolId]);
  useEffect(() => { assignedSchoolIdRef.current = assignedSchoolId; }, [assignedSchoolId]);

  useEffect(() => {
    console.log('[SchoolContext] 🔄 Effect running');
    console.log('[SchoolContext] Auth state:', {
      authInitializing,
      hasUser: !!user,
      userId: user?.uid || 'none'
    });

    // Wait for auth to initialize
    if (authInitializing) {
      console.log('[SchoolContext] ⏸️  Waiting for auth to initialize');
      return;
    }

    // If no user, clear school data
    if (!user) {
      console.log('[SchoolContext] 🚫 No user - clearing school data');
      setSchoolId(null);
      setMembershipSchoolId(null);
      setDeveloperSchoolId(null);
      setAssignedSchoolId(null);
      setSchool(null);
      setAssignedSchool(null);
      setLoading(false);
      listenerHasFiredRef.current = false; // Reset for next user
      // Clear localStorage cache
      try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith(SCHOOL_CACHE_KEY)) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        // Ignore errors
      }
      return;
    }

    // Check if we have cached data
    const cachedData = loadSchoolFromCache(user.uid);
    console.log('[SchoolContext] Cache check:', cachedData ? '✅ HIT' : '❌ MISS');

    // Fallback function to use original Firestore method
    const setupFirestoreListener = () => {
      console.log('[SchoolContext] 📡 Setting up Firestore listener (fallback)');
      const userDocRef = doc(db, 'users', user.uid);

      return onSnapshot(
        userDocRef,
        async (userDocSnap) => {
          console.log('[SchoolContext] 🔔 Firestore update received');
          try {
            if (!userDocSnap.exists()) {
              console.warn('[SchoolContext] User document not found');
              setSchoolId(null);
              setAssignedSchoolId(null);
              setSchool(null);
              setAssignedSchool(null);
              setLoading(false);
              return;
            }

            const userData = userDocSnap.data();
            const membershipId = userData?.schoolId || null;
            const userAssignedSchoolId = userData?.assignedSchoolId;
            const selectedDeveloperSchoolId = userData?.dev
              ? getDeveloperSchoolSelection(user.uid)
              : null;
            if (!userData?.dev) setDeveloperSchoolSelection(user.uid, null);
            const activeSchoolId = resolveSchoolContextId({
              membershipSchoolId: membershipId,
              isPlatformDeveloper: userData?.dev === true,
              developerSchoolId: selectedDeveloperSchoolId,
            });

            console.log('[SchoolContext] Firestore data:', {
              schoolId: activeSchoolId || 'null',
              membershipSchoolId: membershipId || 'null',
              assignedSchoolId: userAssignedSchoolId || 'null'
            });

            setMembershipSchoolId(membershipId);
            setDeveloperSchoolId(selectedDeveloperSchoolId);
            // Update assigned school ID
            setAssignedSchoolId(userAssignedSchoolId || null);

            // Load assigned school data if it exists and different from active
            let assignedSchoolData = null;
            if (userAssignedSchoolId && userAssignedSchoolId !== membershipId) {
              assignedSchoolData = await schoolService.getSchool(userAssignedSchoolId);
              setAssignedSchool(assignedSchoolData);
            } else if (userAssignedSchoolId === membershipId && membershipId === activeSchoolId) {
              // Will be set from active school below
              assignedSchoolData = null;
            } else {
              setAssignedSchool(null);
            }

            // Update active school ID
            setSchoolId(activeSchoolId || null);

            // Load active school data if it exists
            let schoolData = null;
            if (activeSchoolId) {
              schoolData = await schoolService.getSchool(activeSchoolId);
              if (schoolData) {
                console.log('[SchoolContext] Loaded active school:', schoolData.name);
                setSchool(schoolData);

                // If assigned is same as active, set it
                if (userAssignedSchoolId === membershipId && membershipId === activeSchoolId) {
                  setAssignedSchool(schoolData);
                  assignedSchoolData = schoolData;
                }
              } else {
                console.error('[SchoolContext] Active school not found:', activeSchoolId);
                setSchool(null);
              }
            } else {
              console.log('[SchoolContext] No active school');
              setSchool(null);
            }

            // Cache all school context data (no team context in fallback mode)
            saveSchoolToCache(user.uid, {
              schoolId: activeSchoolId || null,
              membershipSchoolId: membershipId,
              developerSchoolId: selectedDeveloperSchoolId,
              assignedSchoolId: userAssignedSchoolId || null,
              school: schoolData,
              assignedSchool: assignedSchoolData,
              organizationalContext: null // Fallback mode doesn't load team context
            });

            setLoading(false);
          } catch (err) {
            console.error('[SchoolContext] Error in listener:', err);
            setError(err?.message || 'Unknown error loading school');
            setLoading(false);
          }
        },
        (err) => {
          console.error('[SchoolContext] Listener error:', err);
          setError(err?.message || 'Unknown error');
          setLoading(false);
        }
      );
    };

    // Only call function if no cache
    if (!cachedData) {
      setLoading(true);
      setError(null);

      console.log('[SchoolContext] 🚀 Calling getUserInitData Cloud Function');

      const getUserInitData = httpsCallable(functions, 'getUserInitData');

      // Phase 2: Add timeout protection for Cloud Function call
      const getUserInitDataWithTimeout = async () => {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('School context loading timeout')), 10000)
        );

        try {
          const result = await Promise.race([getUserInitData(), timeoutPromise]);
          return result;
        } catch (error) {
          console.error('[SchoolContext] ❌ getUserInitData failed:', error.message);

          // Special handling for 404 errors (user document not found) - common during registration
          if (error.message?.includes('User document not found') || error.code === 'not-found') {
            console.log('[SchoolContext] ⚠️ User document not yet available (likely still replicating)');
            console.log('[SchoolContext] 📡 Will rely on real-time listener for data');
          }

          // Force fallback to cached data or empty state
          const fallbackData = loadSchoolFromCache(user.uid) || {
            user: { schoolId: null, assignedSchoolId: null },
            school: null,
            assignedSchool: null,
            permissions: { teacher: true }, // Default fallback
            menuItems: [],
            organizationalContext: null,
            parent: null // Add parent fallback
          };

          // Don't throw error - return fallback data instead
          return { data: fallbackData };
        }
      };

      getUserInitDataWithTimeout()
        .then(async (result) => {
          console.log('[SchoolContext] ✅ Function returned successfully');
          const data = result.data;

          // Validate response structure
          if (!data || !data.user) {
            console.error('[SchoolContext] ❌ Invalid response structure from getUserInitData:', data);
            throw new Error('Invalid response structure - missing user data');
          }

          console.log('[SchoolContext] Function data:', {
            hasUser: !!data.user,
            hasSchool: !!data.school,
            hasAssignedSchool: !!data.assignedSchool,
            menuItemsCount: data.menuItems?.length || 0,
            hasParentData: !!data.parent,
            hasOrgContext: !!data.organizationalContext
          });

          const membershipId = data.user?.schoolId || null;
          const selectedDeveloperSchoolId = data.permissions?.dev
            ? getDeveloperSchoolSelection(user.uid)
            : null;
          if (!data.permissions?.dev) setDeveloperSchoolSelection(user.uid, null);
          const activeSchoolId = resolveSchoolContextId({
            membershipSchoolId: membershipId,
            isPlatformDeveloper: data.permissions?.dev === true,
            developerSchoolId: selectedDeveloperSchoolId,
          });
          const activeSchool = activeSchoolId && activeSchoolId !== membershipId
            ? await schoolService.getSchool(activeSchoolId)
            : data.school || null;

          // Set all state from function response with null-safety
          setSchoolId(activeSchoolId);
          setMembershipSchoolId(membershipId);
          setDeveloperSchoolId(selectedDeveloperSchoolId);
          setAssignedSchoolId(data.user?.assignedSchoolId || null);
          setSchool(activeSchool);
          setAssignedSchool(data.assignedSchool || null);

          // Only update permissions if real-time listener hasn't fired yet
          // This prevents Cloud Function response from overwriting correct listener data during race conditions
          if (!listenerHasFiredRef.current) {
            console.log('[SchoolContext] 💾 Setting permissions from Cloud Function (listener not fired yet)');
            setPermissions(data.permissions || null);
          } else {
            console.log('[SchoolContext] ⏭️ Skipping Cloud Function permissions (listener has priority)');
          }

          setMenuItems(data.menuItems || null);
          setOrganizationalContext(data.organizationalContext || null);

          // Initialize parent store if parent data exists
          if (data.parent) {
            console.log('[SchoolContext] 🔵 Initializing parent store from Cloud Function', {
              hasProfile: !!data.parent.profile,
              linkedChildrenCount: data.parent.linkedChildren?.length || 0
            });
            useParentStore.getState().setInitialData(data.parent);
          } else if (data.permissions?.parent) {
            // Cloud Function returned no parent data but user has parent role - fallback
            console.log('[SchoolContext] 🔵 No parent data from CF, falling back to initializeParent');
            useParentStore.getState().initializeParent(user.uid);
          }

          // Save to cache (including parent data and team context)
          saveSchoolToCache(user.uid, {
            schoolId: activeSchoolId,
            membershipSchoolId: membershipId,
            developerSchoolId: selectedDeveloperSchoolId,
            assignedSchoolId: data.user?.assignedSchoolId || null,
            school: activeSchool,
            assignedSchool: data.assignedSchool || null,
            permissions: data.permissions || null,
            menuItems: data.menuItems || [],
            parent: data.parent || null,
            organizationalContext: data.organizationalContext || null
          });

          setLoading(false);
          console.log('[SchoolContext] 💾 Data cached from Cloud Function');
        })
        .catch((error) => {
          console.error('[SchoolContext] ❌ Cloud Function failed:', error);
          console.error('[SchoolContext] Error details:', {
            code: error.code,
            message: error.message,
            details: error.details
          });

          // Phase 2: Add timeout to fallback and ensure loading completes
          console.log('[SchoolContext] 🔄 Falling back to Firestore listener');
          setError('Cloud Function failed - using fallback');

          // Set up Firestore listener as fallback
          const unsubscribe = setupFirestoreListener();
          
          // Phase 2: Add timeout for fallback listener
          setTimeout(() => {
            console.warn('[SchoolContext] ⚠️ FALLBACK TIMEOUT - Forcing loading to complete');
            setLoading(false); // Force completion to prevent infinite loading
          }, 15000);

          // Store unsubscribe in ref so cleanup can access it
          return () => {
            console.log('[SchoolContext] 🧹 Cleaning up fallback listener');
            unsubscribe();
          };
        });
    } else {
      console.log('[SchoolContext] 📦 Using cached data');

      // Load cached data into state
      setSchoolId(cachedData.schoolId || null);
      setMembershipSchoolId(cachedData.membershipSchoolId || cachedData.schoolId || null);
      setDeveloperSchoolId(cachedData.developerSchoolId || null);
      setAssignedSchoolId(cachedData.assignedSchoolId || null);
      setSchool(cachedData.school || null);
      setAssignedSchool(cachedData.assignedSchool || null);
      setPermissions(cachedData.permissions || null);
      setMenuItems(cachedData.menuItems || null);
      setOrganizationalContext(cachedData.organizationalContext || null);

      // Initialize parent store from cached parent data
      if (cachedData.parent) {
        console.log('[SchoolContext] 🔵 Initializing parent store from cached data', {
          hasProfile: !!cachedData.parent.profile,
          linkedChildrenCount: cachedData.parent.linkedChildren?.length || 0
        });
        useParentStore.getState().setInitialData(cachedData.parent);
      } else if (cachedData.permissions?.parent) {
        // Cache has no parent data but user has parent role - fallback to direct init
        console.log('[SchoolContext] 🔵 No cached parent data, falling back to initializeParent');
        useParentStore.getState().initializeParent(user.uid);
      }

      setLoading(false);
    }

    // Set up real-time listener for updates (runs regardless of cache)
    console.log('[SchoolContext] 📡 Setting up real-time listener for updates');
    const userDocRef = doc(db, 'users', user.uid);

    const unsubscribe = onSnapshot(
      userDocRef,
      async (userDocSnap) => {
        console.log('[SchoolContext] 🔔 Real-time update received');

        // Mark that listener has fired - don't let Cloud Function overwrite after this
        listenerHasFiredRef.current = true;

        if (!userDocSnap.exists()) {
          console.warn('[SchoolContext] User document not found in update');
          return;
        }

        const userData = userDocSnap.data();
        const userAssignedSchoolId = userData?.assignedSchoolId;

        // Extract permission flags from userData — school-scoped with legacy fallback
        const membershipId = userData?.schoolId || null;
        const selectedDeveloperSchoolId = userData?.dev
          ? getDeveloperSchoolSelection(user.uid)
          : null;
        if (!userData?.dev) setDeveloperSchoolSelection(user.uid, null);
        const activeSchoolId = resolveSchoolContextId({
          membershipSchoolId: membershipId,
          isPlatformDeveloper: userData?.dev === true,
          developerSchoolId: selectedDeveloperSchoolId,
        });
        const schoolEntry = membershipId
          ? (userData?.schoolRoles?.[membershipId] ?? null)
          : null;

        const legacyAdmin = userData?.admin || false;
        const legacyStaff = userData?.staff || false;
        const accessRole = schoolEntry?.accessRole
          ?? (legacyAdmin ? 'admin' : legacyStaff ? 'staff' : null);

        const newPermissions = {
          accessRole,
          customRoleId: schoolEntry?.customRoleId ?? null,
          canManageParentLinking:
            schoolEntry?.canManageParentLinking === true ||
            userData?.canManageParentLinking === true,
          admin: accessRole === 'admin',
          staff: accessRole === 'staff' || accessRole === 'admin',
          dev: userData?.dev || false,
          parent: userData?.parent || false,
          setupComplete: userData?.setupComplete || false,
          userType: userData?.userType || null,
          photoURL: userData?.photoURL || null,
          displayName: userData?.displayName || null,
          firstName: userData?.firstName || null,
          lastName: userData?.lastName || null,
          email: userData?.email || null,
        };

        // Check if permissions changed (handle initial null state)
        const permissionsChanged = !permissions || (
          permissions.admin !== newPermissions.admin ||
          permissions.dev !== newPermissions.dev ||
          permissions.staff !== newPermissions.staff ||
          permissions.parent !== newPermissions.parent ||
          permissions.setupComplete !== newPermissions.setupComplete ||
          permissions.userType !== newPermissions.userType ||
          permissions.accessRole !== newPermissions.accessRole ||
          permissions.customRoleId !== newPermissions.customRoleId ||
          permissions.canManageParentLinking !== newPermissions.canManageParentLinking ||
          permissions.firstName !== newPermissions.firstName ||
          permissions.lastName !== newPermissions.lastName ||
          permissions.displayName !== newPermissions.displayName ||
          permissions.photoURL !== newPermissions.photoURL
        );

        // Check if school changed — use refs to avoid stale closure
        const schoolChanged = activeSchoolId !== schoolIdRef.current ||
          membershipId !== membershipSchoolIdRef.current ||
          userAssignedSchoolId !== assignedSchoolIdRef.current;

        // Always update permissions when listener fires (listener is source of truth)
        if (permissionsChanged) {
          console.log('[SchoolContext] 🔄 Permissions changed, updating...', {
            old: permissions ? { admin: permissions.admin, dev: permissions.dev, parent: permissions.parent, setupComplete: permissions.setupComplete } : 'null',
            new: { admin: newPermissions.admin, dev: newPermissions.dev, parent: newPermissions.parent, setupComplete: newPermissions.setupComplete }
          });
          setPermissions(newPermissions);
        }

        if (schoolChanged) {
          console.log('[SchoolContext] 🔄 School changed, updating...', {
            oldSchoolId: schoolId,
            newSchoolId: activeSchoolId,
            oldMembershipSchoolId: membershipSchoolId,
            newMembershipSchoolId: membershipId,
            oldAssignedSchoolId: assignedSchoolId,
            newAssignedSchoolId: userAssignedSchoolId
          });

          // Update IDs immediately
          setSchoolId(activeSchoolId || null);
          setMembershipSchoolId(membershipId);
          setDeveloperSchoolId(selectedDeveloperSchoolId);
          setAssignedSchoolId(userAssignedSchoolId || null);

          // Fetch school docs if needed
          let assignedSchoolData = null;
          if (userAssignedSchoolId && userAssignedSchoolId !== membershipId) {
            assignedSchoolData = await schoolService.getSchool(userAssignedSchoolId);
            setAssignedSchool(assignedSchoolData);
          } else if (userAssignedSchoolId === membershipId && membershipId === activeSchoolId) {
            // Will be set from active school below
            assignedSchoolData = null;
          } else {
            setAssignedSchool(null);
          }

          let schoolData = null;
          if (activeSchoolId) {
            schoolData = await schoolService.getSchool(activeSchoolId);
            setSchool(schoolData);

            // If assigned is same as active, set it
            if (userAssignedSchoolId === membershipId && membershipId === activeSchoolId) {
              setAssignedSchool(schoolData);
              assignedSchoolData = schoolData;
            }
          } else {
            setSchool(null);
          }

          // Update cache with both school and permissions (preserve team context)
          saveSchoolToCache(user.uid, {
            schoolId: activeSchoolId || null,
            membershipSchoolId: membershipId,
            developerSchoolId: selectedDeveloperSchoolId,
            assignedSchoolId: userAssignedSchoolId || null,
            school: schoolData,
            assignedSchool: assignedSchoolData,
            permissions: newPermissions,
            organizationalContext // Preserve existing team context
          });

          console.log('[SchoolContext] ✅ Real-time update complete');
        } else if (permissionsChanged) {
          // This listener may have started before cached school state hydrated.
          // Its captured school fields can still be null. Preserve the saved
          // context and use the snapshot's unchanged IDs when refreshing permissions.
          saveSchoolToCache(user.uid, {
            ...loadSchoolFromCache(user.uid),
            schoolId: activeSchoolId || null,
            membershipSchoolId: membershipId,
            developerSchoolId: selectedDeveloperSchoolId,
            assignedSchoolId: userAssignedSchoolId || null,
            permissions: newPermissions,
          });
          console.log('[SchoolContext] ✅ Permission update complete');
        }
      },
      (err) => {
        console.error('[SchoolContext] ❌ Real-time listener error:', err);
      }
    );

    // Cleanup listener on unmount
    return () => {
      console.log('[SchoolContext] 🧹 Cleaning up real-time listener');
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, authInitializing]); // Only re-run when user changes, not when schoolId changes

  const selectDeveloperSchool = async (nextSchoolId) => {
    if (!user?.uid || permissions?.dev !== true) {
      throw new Error('Platform Developer access is required');
    }

    const selectedSchool = nextSchoolId
      ? await schoolService.getSchool(nextSchoolId)
      : null;
    if (nextSchoolId && !selectedSchool) {
      throw new Error('The selected school does not exist');
    }

    const membershipId = membershipSchoolIdRef.current;
    const activeSchoolId = resolveSchoolContextId({
      membershipSchoolId: membershipId,
      isPlatformDeveloper: true,
      developerSchoolId: nextSchoolId,
    });
    const activeSchool = nextSchoolId
      ? selectedSchool
      : membershipId
        ? await schoolService.getSchool(membershipId)
        : null;

    setDeveloperSchoolSelection(user.uid, nextSchoolId);
    setDeveloperSchoolId(nextSchoolId || null);
    setSchoolId(activeSchoolId);
    setSchool(activeSchool);
    saveSchoolToCache(user.uid, {
      schoolId: activeSchoolId,
      membershipSchoolId: membershipId,
      developerSchoolId: nextSchoolId || null,
      assignedSchoolId,
      school: activeSchool,
      assignedSchool,
      permissions,
      menuItems,
      organizationalContext,
    });
    return activeSchool;
  };

  const value = {
    schoolId,
    school,
    membershipSchoolId,
    developerSchoolId,
    selectDeveloperSchool,
    assignedSchoolId,
    assignedSchool,
    permissions,
    menuItems,
    organizationalContext, // { memberships, teams, primaryTeam, permissions, isLeaderOfAnyTeam }
    loading,
    error,
    // Helper method to check if school is loaded
    hasSchool: !!schoolId && !!school,
    // Helper to check if user has an assigned school (even if in Personal workspace)
    hasAssignedSchool: !!assignedSchoolId && !!assignedSchool,
    // Helper method for migration - indicates user needs school assignment
    needsSchoolAssignment: !loading && !!user && !schoolId && !assignedSchoolId,
    // Helper to check if user has team memberships
    hasOrgContext: !!organizationalContext && organizationalContext.memberships?.length > 0
  };

  return (
    <SchoolContext.Provider value={value}>
      {children}
    </SchoolContext.Provider>
  );
};
